import { clip, type Bones, type Clip } from './clip.ts';
import { TAU, inCubic, inOut, inQuad, outBack, outCubic, outExpo, outQuad, phase, settle, span } from './curves.ts';
import { apply, timeline, type Beat, type KeyPose } from './pose.ts';

/**
 * Clips for the humanoid figure (heroes, the faceless king, the saga's people). Conventions (radians):
 * arms and legs hang along -Y, so a negative X turns them forward; a positive X on a shin bends the knee
 * back, a negative X on a forearm bends the elbow forward; +X on the body leans it forward; the figure
 * faces +Z; the `R` limbs are on +X.
 *
 * The `weapon` and `offhand` X channels are absolute: the angle of the blade (or shield, bow, staff) from
 * straight up, toward the front (π/2 points it ahead, π down), whatever the arm does. At rest every weapon
 * stands straight up from the fist.
 */
export const HUMAN_BONES = ['root', 'figure', 'body', 'head', 'armL', 'foreL', 'offhand', 'armR', 'foreR', 'weapon', 'legL', 'shinL', 'legR', 'shinR', 'cape'] as const;
export type HB = (typeof HUMAN_BONES)[number];
type P = KeyPose<HB>;
type B = Bones<HB>;

/** Turns the absolute weapon/offhand angles into the hand's own rotation. */
const aim = (b: B): void => {
  b.weapon.rx -= b.armR.rx + b.foreR.rx + b.body.rx;
  b.offhand.rx -= b.armL.rx + b.foreL.rx + b.body.rx;
};

const hclip = (name: string, dur: number, loop: boolean, pose: (b: B, t: number) => void, squash?: (t: number) => [number, number, number]): Clip<HB> =>
  clip<HB>(name, dur, loop, (b, t) => {
    pose(b, t);
    aim(b);
  }, squash);

/** How each class holds itself and its gear: layered under idle and walk, and where one-shots start and end. */
export interface Style {
  guard: P;
  /** How much the arms swing when walking (0 still, 1 free). */
  armSwing: number;
  /** Heavy figures bob more and plant harder. */
  weight: number;
  /** Takes blows on a shield (hit = block). */
  shield?: boolean;
}

const BASE: P = {
  legL: [-0.03, 0, -0.03],
  legR: [0.04, 0, 0.03],
  shinL: [0.07, 0, 0],
  shinR: [0.07, 0, 0],
  figure: [0, 0, 0, 0, -0.006, 0],
};

/** Sums key poses channel by channel (scale multiplies). */
const sum = (...ps: P[]): P => {
  const out: Partial<Record<HB, number[]>> = {};
  for (const p of ps) {
    for (const [k, c] of Object.entries(p) as [HB, readonly number[]][]) {
      const cur = out[k] ?? [0, 0, 0, 0, 0, 0, 1];
      for (let i = 0; i < 7; i++) {
        const v = c[i] ?? (i === 6 ? 1 : 0);
        cur[i] = i === 6 ? (cur[i] as number) * v : (cur[i] as number) + v;
      }
      out[k] = cur;
    }
  }
  return out as unknown as P;
};

/* ---------- loops ---------- */

const idle = (s: Style): Clip<HB> => {
  const stance = sum(BASE, s.guard);
  return hclip('idle', 4, true, (b, t) => {
    const p = phase(t, 4) * TAU;
    apply(b, stance);
    const br = Math.sin(p * 2);
    // Two breaths a loop, a slow weight shift from foot to foot, a glance around now and then.
    b.body.rot(-0.02 * br, 0.06 * Math.sin(p + 0.6), 0.022 * Math.sin(p)).scale(1 + 0.007 * br);
    b.figure.rot(0, 0, 0.012 * Math.sin(p)).pos(0, -0.005 * (1 + br), 0);
    b.legL.rot(0, 0, -0.012 * Math.sin(p));
    b.legR.rot(0, 0, -0.012 * Math.sin(p));
    const look = Math.max(0, Math.sin(p + 1.5)) ** 2;
    b.head.rot(0.035 * br + 0.03 * Math.sin(p * 3 + 1), 0.3 * Math.sin(p * 2 + 2.1) * look, -0.025 * Math.sin(p));
    b.armL.rot(0.03 * br, 0, -0.025 * br);
    b.armR.rot(0.03 * br, 0, 0.025 * br);
    b.foreL.rot(-0.04 * Math.sin(p * 2 + 0.5));
    b.foreR.rot(-0.04 * Math.sin(p * 2 + 0.5));
    b.cape.rot(0.035 + 0.03 * Math.sin(p * 2 + 1.2), 0.025 * Math.sin(p + 0.4));
    b.weapon.rot(0.04 * Math.sin(p * 2 + 0.9));
    b.offhand.rot(0.03 * Math.sin(p * 2 + 1.3));
  });
};

/** One stride cycle: two steps. `dur` is a cycle at walking pace (the view scales it by the ground covered). */
const walk = (s: Style, dur = 0.84): Clip<HB> => {
  const stance = sum(BASE, s.guard);
  const heavy = s.weight;
  return hclip('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    const sn = Math.sin(th);
    const cs = Math.cos(th);
    apply(b, stance, 0.9);
    // Legs: the thigh swings, the knee folds through the swing and gives on contact; the stance leg drives back.
    const A = 0.5;
    b.legL.rot(-A * sn - 0.04);
    b.legR.rot(A * sn - 0.04);
    const fold = (c: number, s2: number): number => 1.0 * Math.max(0, c) ** 1.5 + 0.18 * Math.max(0, s2) ** 4 + 0.06;
    b.shinL.rot(fold(cs, -sn));
    b.shinR.rot(fold(-cs, sn));
    // Hips: down on contact, up passing, a roll over the standing leg.
    b.figure.pos(0, 0.03 * (1 + heavy * 0.7) * Math.cos(2 * th) - 0.02 - 0.012 * heavy, 0).rot(0, 0, 0.02 * sn);
    b.body.rot(0.06 + 0.04 * heavy - 0.025 * Math.cos(2 * th - 0.4), -0.12 * sn, 0.03 * sn);
    // The head steadies against the torso and dips a little after each contact.
    b.head.rot(-0.03 + 0.03 * Math.cos(2 * th - 0.8), 0.09 * sn, -0.02 * sn);
    // Arms swing against the legs and trail behind them; elbows fold on the forward swing.
    const lagA = Math.sin(th - 0.4);
    const sw = 0.45 * s.armSwing;
    b.armL.rot(sw * lagA, 0, -0.03);
    b.armR.rot(-sw * lagA, 0, 0.03);
    b.foreL.rot(-0.25 * s.armSwing * Math.max(0, -lagA));
    b.foreR.rot(-0.25 * s.armSwing * Math.max(0, lagA));
    // Gear sways a beat behind the hand.
    b.weapon.rot(0.08 * Math.sin(th - 1.3));
    b.offhand.rot(0.06 * Math.sin(th - 1.3));
    // The cape streams back and flutters twice a stride.
    b.cape.rot(0.2 + 0.08 * Math.cos(2 * th - 1.3), 0.06 * Math.sin(th - 1.5), 0.03 * Math.sin(th - 1.5));
  });
};

/* ---------- reactions ---------- */

/** A blow lands: snap back, then spring home. */
const flinch = (s: Style): Clip<HB> => {
  const stance = sum(BASE, s.guard);
  return hclip('hit', 0.45, false, (b, t) => {
    apply(b, stance);
    const k = settle(t, 2.2, 7) * span(t, 0, 0.035, outQuad);
    const a = Math.abs(k);
    b.body.rot(-0.34 * k, 0.14 * k, 0.07 * k);
    b.head.rot(-0.4 * k, -0.18 * k, 0.05 * k);
    b.armL.rot(0.3 * k, 0, -0.32 * k);
    b.armR.rot(0.22 * k, 0, 0.3 * k);
    b.foreL.rot(-0.3 * a);
    b.foreR.rot(-0.2 * a);
    b.figure.pos(0, -0.035 * a, -0.07 * k);
    b.shinL.rot(0.22 * a);
    b.shinR.rot(0.28 * a);
    b.legR.rot(0.12 * k);
    b.cape.rot(0.3 * k);
  });
};

/** Takes it on the shield: the shield snaps up, the body braces and gives a little. */
const block = (s: Style): Clip<HB> => {
  const stance = sum(BASE, s.guard);
  const up: P = { armL: [-0.75, 0, 0.5], foreL: [-1.05, 0.35, 0], offhand: [0.05, 0.45, 0], armR: [-0.1, 0, 0.15], body: [0.12, -0.3, 0], head: [0.1, -0.1, 0], legL: [-0.35, 0, 0], shinL: [0.3, 0, 0], legR: [0.3, 0, 0], shinR: [0.25, 0, 0], figure: [0, 0, 0, 0, -0.05, 0] };
  return hclip('hit', 0.5, false, (b, t) => {
    timeline(b, t, [[0, stance], [0.06, sum(stance, up), outExpo], [0.3, sum(stance, up)], [0.5, stance, inOut]]);
    // The shock: the shield arm knocked back and the heels sliding.
    const k = t > 0.05 ? settle(t - 0.05, 3, 9) : 0;
    b.armL.rot(0.18 * k);
    b.body.rot(-0.1 * k);
    b.figure.pos(0, 0, -0.05 * Math.max(0, k));
  });
};

/** Knees buckle, the figure falls on its back and settles (held while down). */
const down = (s: Style): Clip<HB> => {
  const stance = sum(BASE, s.guard);
  return hclip('down', 1.1, false, (b, t) => {
    apply(b, stance, 1 - span(t, 0, 0.35));
    const buckle = span(t, 0, 0.22, outQuad);
    const fall = span(t, 0.18, 0.6, inQuad);
    const bounce = t > 0.6 ? Math.abs(settle(t - 0.6, 2.4, 6)) * 0.1 : 0;
    b.shinL.rot(0.95 * buckle * (1 - fall * 0.8));
    b.shinR.rot(0.75 * buckle * (1 - fall * 0.8));
    b.legL.rot(-0.55 * buckle * (1 - fall) + fall * 0.05, 0, -0.14 * fall);
    b.legR.rot(-0.35 * buckle * (1 - fall), 0, 0.1 * fall);
    b.figure.rot(-1.45 * fall + bounce).pos(0, -0.2 * buckle * (1 - fall) + 0.2 * fall, -0.08 * fall);
    b.body.rot(0.4 * buckle * (1 - fall) - 0.05 * fall);
    b.head.rot(0.35 * buckle * (1 - fall) + 0.25 * fall - bounce * 1.5, 0.55 * fall);
    b.armL.rot(-0.4 * buckle * (1 - fall), 0, -0.8 * fall - bounce);
    b.armR.rot(-0.3 * buckle * (1 - fall), 0, 0.9 * fall + bounce);
    b.foreL.rot(-0.3 * fall);
    b.foreR.rot(-0.2 * fall);
    // The weapon slips to lie along the arm.
    b.weapon.rot(1.2 * fall);
    b.cape.rot(-0.6 * fall);
  });
};

/** Hammering at a build: raise, a fast blow that lands at 0.375 s (the dust), the jolt, raise again. */
const work = (): Clip<HB> => {
  const up: P = { armR: [-2.5, 0, 0.1], foreR: [-0.8, 0, 0], weapon: [-1.6, 0, 0], armL: [-0.7, 0, 0.25], foreL: [-0.6, 0, 0], body: [-0.05, -0.15, 0], head: [-0.06, 0, 0], legL: [-0.15, 0, 0], legR: [0.12, 0, 0] };
  const blow: P = { armR: [-1.15, 0, -0.1], foreR: [-0.2, 0, 0], weapon: [2.0, 0, 0], armL: [-0.75, 0, 0.3], foreL: [-0.5, 0, 0], body: [0.4, 0.08, 0], head: [0.32, 0, 0], figure: [0, 0, 0, 0, -0.05, 0], legL: [-0.25, 0, 0], shinL: [0.3, 0, 0], legR: [0.2, 0, 0], shinR: [0.25, 0, 0] };
  return hclip('work', 0.5, true, (b, t) => {
    timeline(b, t, [[0, blow], [0.14, up, outCubic], [0.29, up], [0.375, blow, inCubic], [0.5, blow]], { weapon: 0.025, head: 0.02 });
    if (t > 0.375) b.body.rot(0.05 * settle(t - 0.375, 6, 14));
  });
};

/* ---------- one-shot builder ---------- */

/** Lags for one-shots: hand and gear trail the arm, the head trails the body, the cape trails everything. */
const LAG: Partial<Record<HB, number>> = { weapon: 0.016, offhand: 0.016, foreR: 0.008, foreL: 0.008, head: 0.025, cape: 0.07 };

/**
 * A one-shot from the guard and back: `beats` are offsets layered on the guard. The simulation deals the blow
 * when the clip starts, so every attack lands by ~0.14 s: a snap of anticipation, the strike, a long
 * follow-through and recovery.
 */
const shot = (name: string, s: Style, dur: number, beats: readonly Beat<HB>[], extra?: (b: B, t: number) => void, squash?: (t: number) => [number, number, number]): Clip<HB> => {
  const g = sum(BASE, s.guard);
  const full: Beat<HB>[] = [[0, g], ...beats.map(([t, p, e]): Beat<HB> => (e ? [t, sum(g, p), e] : [t, sum(g, p)])), [dur, g, inOut]];
  return hclip(name, dur, false, (b, t) => {
    timeline(b, t, full, LAG);
    extra?.(b, t);
  }, squash);
};

/* ---------- the classes ---------- */

export type HeroClipSet = 'warrior' | 'artificer' | 'mage' | 'ranger' | 'cleric' | 'barbarian' | 'paladin' | 'druid' | 'king' | 'folk';

// Guards: the sword arm cocked with the blade up and forward, the shield across the body.
const SWORD_GUARD: P = { armR: [-0.25, 0, 0.12], foreR: [-1.0, 0, 0], weapon: [0.45, 0, 0], armL: [-0.4, 0, -0.08], foreL: [-0.8, 0.2, 0.1], offhand: [0, 0.35, 0] };
const STAFF_GUARD: P = { armR: [-0.18, 0, 0.1], foreR: [-0.65, 0, 0], weapon: [0.05, 0, 0], armL: [-0.08, 0, -0.08], foreL: [-0.45, 0, 0] };

export const STYLES: Record<HeroClipSet, Style> = {
  warrior: { guard: SWORD_GUARD, armSwing: 0.4, weight: 0.6, shield: true },
  paladin: { guard: { ...SWORD_GUARD, weapon: [0.3, 0, 0] }, armSwing: 0.35, weight: 0.9, shield: true },
  // The artificer carries the hammer low, head forward; the gauntlet hand loose.
  artificer: { guard: { armR: [-0.05, 0, 0.1], foreR: [-0.7, 0, 0], weapon: [1.1, 0, 0], armL: [-0.1, 0, -0.1], foreL: [-0.5, 0, 0] }, armSwing: 0.75, weight: 0.3 },
  mage: { guard: STAFF_GUARD, armSwing: 0.45, weight: 0.1 },
  cleric: { guard: { ...STAFF_GUARD, foreL: [-0.6, 0, 0], offhand: [0.1, 0, 0] }, armSwing: 0.45, weight: 0.2 },
  druid: { guard: STAFF_GUARD, armSwing: 0.5, weight: 0.2 },
  // The bow held low and forward in the left hand, the right hand near the quiver.
  ranger: { guard: { armL: [-0.2, 0, -0.1], foreL: [-0.45, 0, 0], offhand: [0.25, 0, 0], armR: [0.05, 0, 0.08], foreR: [-0.35, 0, 0] }, armSwing: 0.8, weight: 0.1 },
  // The axe rests on the shoulder, head behind.
  barbarian: { guard: { armR: [-0.35, 0, 0.22], foreR: [-1.75, 0, 0], weapon: [-0.55, 0, 0], armL: [-0.12, 0, -0.15], foreL: [-0.45, 0, 0] }, armSwing: 0.65, weight: 0.8 },
  king: { guard: { armR: [-0.3, 0, 0.1], foreR: [-0.85, 0, 0], weapon: [0.25, 0, 0], armL: [-0.05, 0, -0.08], foreL: [-0.3, 0, 0] }, armSwing: 0.3, weight: 1 },
  folk: { guard: { foreL: [-0.2, 0, 0], foreR: [-0.2, 0, 0] }, armSwing: 1, weight: 0.2 },
};

/** Shared beats. Offsets from the guard. */
const STEP_IN: P = { legL: [-0.4, 0, 0], shinL: [0.3, 0, 0], legR: [0.28, 0, 0], shinR: [0.12, 0, 0], figure: [0, 0, 0, 0, -0.045, 0.08] };
const STEP_IN_HOLD: P = { legL: [-0.36, 0, 0], shinL: [0.28, 0, 0], legR: [0.25, 0, 0], shinR: [0.12, 0, 0], figure: [0, 0, 0, 0, -0.04, 0.07] };

/** A diagonal sword cut: cocked behind the head, down across the body, a follow-through past the hip. */
const swordCut = (s: Style): Clip<HB> =>
  shot('attack', s, 0.62, [
    [0.07, { armR: [-2.35, 0, 0.35], foreR: [-0.35, 0, 0], weapon: [-2.3, 0, 0], body: [-0.1, 0.42, 0], head: [0, -0.18, 0], legR: [0.12, 0, 0] }, outQuad],
    [0.14, { armR: [-1.15, 0, -0.5], foreR: [0.6, 0, 0], weapon: [1.55, 0, 0], body: [0.25, -0.45, 0], head: [-0.05, 0.22, 0], ...STEP_IN }, inQuad],
    [0.28, { armR: [-0.5, 0, -0.7], foreR: [0.7, 0, 0], weapon: [2.5, 0, 0], body: [0.25, -0.6, 0], head: [0, 0.25, 0], ...STEP_IN_HOLD }, outBack],
    [0.4, { armR: [-0.45, 0, -0.62], foreR: [0.55, 0, 0], weapon: [2.4, 0, 0], body: [0.18, -0.5, 0], ...STEP_IN_HOLD }],
  ]);

/** An overhead hammer blow (paladin, artificer): heavier, the body coming down behind it. */
const hammerBlow = (s: Style, dur = 0.7): Clip<HB> =>
  shot('attack', s, dur, [
    [0.08, { armR: [-2.6, 0, 0.15], foreR: [-0.5, 0, 0], weapon: [-1.9, 0, 0], body: [-0.18, 0.2, 0], head: [-0.1, -0.1, 0], figure: [0, 0, 0, 0, 0.02, 0] }, outQuad],
    [0.15, { armR: [-0.95, 0, -0.15], foreR: [0.45, 0, 0], weapon: [2.1, 0, 0], body: [0.5, -0.15, 0], head: [0.25, 0.1, 0], ...STEP_IN, figure: [0, 0, 0, 0, -0.08, 0.08] }, inCubic],
    [0.3, { armR: [-0.7, 0, -0.15], foreR: [0.4, 0, 0], weapon: [2.4, 0, 0], body: [0.45, -0.15, 0], head: [0.2, 0.1, 0], ...STEP_IN_HOLD, figure: [0, 0, 0, 0, -0.07, 0.07] }, outBack],
    [0.45, { armR: [-0.6, 0, -0.1], foreR: [0.3, 0, 0], weapon: [2.2, 0, 0], body: [0.3, -0.1, 0], ...STEP_IN_HOLD }],
  ], (b, t) => {
    // The jolt of the head striking home.
    if (t > 0.15) b.body.rot(0.05 * settle(t - 0.15, 5, 12));
  });

/** The barbarian's two-handed chop: axe whipped off the shoulder, both hands, the whole body behind it. */
const axeChop = (s: Style): Clip<HB> =>
  shot('attack', s, 0.66, [
    [0.07, { armR: [-1.9, 0, -0.1], foreR: [1.0, 0, 0], weapon: [-2.2, 0, 0], armL: [-2.2, 0, 0.55], foreL: [-0.4, 0, 0], body: [-0.22, 0.3, 0], head: [-0.12, -0.12, 0], figure: [0, 0, 0, 0, 0.02, 0] }, outQuad],
    [0.14, { armR: [-1.05, 0, -0.4], foreR: [1.6, 0, 0], weapon: [2.2, 0, 0], armL: [-1.1, 0, 0.6], foreL: [-0.3, 0, 0], body: [0.55, -0.35, 0], head: [0.2, 0.15, 0], ...STEP_IN, figure: [0, 0, 0, 0, -0.09, 0.12] }, inCubic],
    [0.28, { armR: [-0.6, 0, -0.5], foreR: [1.7, 0, 0], weapon: [2.7, 0, 0], armL: [-0.7, 0, 0.5], foreL: [-0.2, 0, 0], body: [0.5, -0.5, 0], head: [0.15, 0.2, 0], ...STEP_IN_HOLD, figure: [0, 0, 0, 0, -0.08, 0.1] }, outBack],
    [0.42, { armR: [-0.55, 0, -0.4], foreR: [1.6, 0, 0], weapon: [2.55, 0, 0], armL: [-0.6, 0, 0.4], body: [0.35, -0.4, 0], ...STEP_IN_HOLD }],
  ], (b, t) => {
    if (t > 0.14) b.body.rot(0.06 * settle(t - 0.14, 5, 12));
  });

/** A staff thrust that looses a bolt (mage, cleric, druid): the staff swept forward and checked hard. */
const staffShot = (s: Style, dur = 0.6): Clip<HB> =>
  shot('attack', s, dur, [
    [0.07, { armR: [-0.4, 0, 0.1], foreR: [-0.9, 0, 0], weapon: [-0.4, 0, 0], armL: [-0.6, 0, -0.2], foreL: [-0.9, 0, 0], body: [-0.12, 0.25, 0], head: [-0.06, -0.1, 0] }, outQuad],
    [0.13, { armR: [-1.15, 0, -0.05], foreR: [-0.1, 0, 0], weapon: [1.0, 0, 0], armL: [-0.7, 0, 0.25], foreL: [-0.5, 0, 0], body: [0.2, -0.2, 0], head: [0.05, 0.08, 0], ...STEP_IN }, outExpo],
    [0.24, { armR: [-1.05, 0, -0.05], foreR: [-0.15, 0, 0], weapon: [0.85, 0, 0], armL: [-0.55, 0, 0.2], foreL: [-0.4, 0, 0], body: [0.12, -0.18, 0], ...STEP_IN_HOLD }, outBack],
  ], (b, t) => {
    // Recoil of the bolt leaving the crystal.
    if (t > 0.13) {
      const k = settle(t - 0.13, 4, 10);
      b.armR.rot(0.12 * k);
      b.weapon.rot(-0.18 * k);
    }
  });

/** Nock, draw, loose: the bow arm comes up level, the string hand snaps back past the ear and lets go. */
const bowShot = (s: Style): Clip<HB> =>
  shot('attack', s, 0.6, [
    [0.06, { armL: [-1.45, 0.25, -0.1], foreL: [0.05, 0, 0], offhand: [0.1, 0, 0.15], armR: [-1.4, -0.35, 0.3], foreR: [-1.9, 0, 0], body: [0, 0.65, 0], head: [0, -0.6, 0] }, outQuad],
    [0.12, { armL: [-1.5, 0.25, -0.1], foreL: [0.05, 0, 0], offhand: [0.1, 0, 0.15], armR: [-1.5, -0.6, 0.55], foreR: [-2.1, 0, 0], body: [0, 0.7, 0], head: [0, -0.65, 0], legL: [-0.2, 0, 0], legR: [0.25, 0, 0] }, inQuad],
    // Loose: the string hand flies back and open.
    [0.17, { armL: [-1.4, 0.2, -0.1], offhand: [0.25, 0, 0.15], armR: [-1.1, -0.9, 0.9], foreR: [-0.9, 0, 0], body: [-0.04, 0.7, 0], head: [-0.03, -0.62, 0], legL: [-0.2, 0, 0], legR: [0.25, 0, 0] }, outExpo],
    [0.34, { armL: [-1.2, 0.15, -0.1], offhand: [0.2, 0, 0.1], armR: [-0.7, -0.6, 0.6], foreR: [-0.7, 0, 0], body: [0, 0.5, 0], head: [0, -0.45, 0] }],
  ]);

/** Plain folk swing what they hold. */
const punch = (s: Style): Clip<HB> =>
  shot('attack', s, 0.55, [
    [0.07, { armR: [0.3, 0, 0.2], foreR: [-1.4, 0, 0], body: [-0.05, 0.3, 0] }, outQuad],
    [0.14, { armR: [-1.4, 0, -0.1], foreR: [-0.1, 0, 0], body: [0.15, -0.3, 0], ...STEP_IN }, inQuad],
    [0.3, { armR: [-1.2, 0, -0.1], foreR: [-0.2, 0, 0], body: [0.1, -0.25, 0], ...STEP_IN_HOLD }, outBack],
  ]);

/* ---------- skills ---------- */

const RAISE_STAFF: P = { armR: [-2.75, 0, -0.12], foreR: [-0.1, 0, 0], weapon: [0, 0, 0], armL: [-0.3, 0, 0.35], head: [-0.42, 0, 0], body: [-0.15, 0, 0], legL: [-0.12, 0, -0.05], legR: [0.08, 0, 0.05] };

const skillsFor = (set: HeroClipSet, s: Style): Clip<HB>[] => {
  switch (set) {
    case 'warrior':
      return [
        // Shield bash: the shield driven forward with a step, the sword cocked back.
        shot('skill0', s, 0.7, [
          [0.07, { armL: [-0.2, 0, 0.25], foreL: [-1.1, 0.3, 0], offhand: [0, 0.5, 0], body: [-0.1, 0.4, 0], figure: [0, 0, 0, 0, 0.0, -0.04] }, outQuad],
          [0.14, { armL: [-1.0, 0, 0.35], foreL: [-0.45, 0.2, 0], offhand: [0, 0.2, 0], armR: [0.3, 0, 0.2], body: [0.3, -0.35, 0], head: [0.1, 0.15, 0], ...STEP_IN, figure: [0, 0, 0, 0, -0.06, 0.16] }, outExpo],
          [0.32, { armL: [-0.95, 0, 0.3], foreL: [-0.5, 0.2, 0], offhand: [0, 0.2, 0], armR: [0.2, 0, 0.2], body: [0.25, -0.3, 0], ...STEP_IN_HOLD, figure: [0, 0, 0, 0, -0.05, 0.14] }, outBack],
        ]),
        // Challenge: sword raised high and beaten on the shield, chest out, a shout.
        shot('skill1', s, 0.95, [
          [0.12, { armR: [-2.7, 0, 0.3], foreR: [-0.3, 0, 0], weapon: [-0.2, 0, 0], armL: [-0.9, 0, -0.4], foreL: [-0.6, 0, 0], body: [-0.2, 0, 0], head: [-0.35, 0, 0], figure: [0, 0, 0, 0, 0.02, 0] }, outBack],
          [0.3, { armR: [-1.4, 0, -0.3], foreR: [-1.2, 0, 0], weapon: [1.4, 0, 0], armL: [-0.9, 0, -0.1], foreL: [-1.1, 0.3, 0], body: [0.15, -0.15, 0], head: [0.1, 0, 0], ...STEP_IN_HOLD }, inCubic],
          [0.46, { armR: [-2.6, 0, 0.3], foreR: [-0.3, 0, 0], weapon: [-0.1, 0, 0], armL: [-0.7, 0, -0.5], body: [-0.25, 0, 0], head: [-0.4, 0, 0] }, outBack],
          [0.7, { armR: [-2.5, 0, 0.3], foreR: [-0.3, 0, 0], weapon: [-0.1, 0, 0], armL: [-0.6, 0, -0.5], body: [-0.2, 0, 0], head: [-0.35, 0, 0] }],
        ]),
        // Oath: the sword raised to the sky in both hands, then brought down before the face.
        shot('skill2', s, 1.1, [
          [0.18, { armR: [-2.9, 0, -0.2], foreR: [-0.1, 0, 0], weapon: [0, 0, 0], armL: [-0.4, 0, -0.3], head: [-0.5, 0, 0], body: [-0.2, 0, 0], figure: [0, 0, 0, 0, 0.03, 0] }, outBack],
          [0.6, { armR: [-2.95, 0, -0.2], foreR: [-0.1, 0, 0], weapon: [0, 0, 0], armL: [-0.4, 0, -0.3], head: [-0.55, 0, 0], body: [-0.22, 0, 0], figure: [0, 0, 0, 0, 0.03, 0] }],
          [0.85, { armR: [-1.1, 0, -0.45], foreR: [-1.4, 0, 0], weapon: [0, 0, 0], armL: [-0.5, 0, -0.1], head: [0.15, 0, 0], body: [0.1, 0, 0] }, inOut],
        ]),
      ];
    case 'paladin':
      return [
        // Smite: a two-handed overhead hammer that craters the ground.
        shot('skill0', s, 0.85, [
          [0.1, { armR: [-2.8, 0, 0.1], foreR: [-0.6, 0, 0], weapon: [-2.0, 0, 0], armL: [-2.4, 0, -0.1], foreL: [-0.6, 0, 0], body: [-0.25, 0, 0], head: [-0.25, 0, 0], figure: [0, 0, 0, 0, 0.06, 0] }, outQuad],
          [0.18, { armR: [-0.85, 0, -0.1], foreR: [0.3, 0, 0], weapon: [2.4, 0, 0], armL: [-0.9, 0, 0.2], foreL: [-0.2, 0, 0], body: [0.6, 0, 0], head: [0.3, 0, 0], ...STEP_IN, figure: [0, 0, 0, 0, -0.14, 0.1] }, inCubic],
          [0.45, { armR: [-0.8, 0, -0.1], foreR: [0.3, 0, 0], weapon: [2.55, 0, 0], armL: [-0.85, 0, 0.2], body: [0.55, 0, 0], head: [0.25, 0, 0], ...STEP_IN_HOLD, figure: [0, 0, 0, 0, -0.12, 0.09] }],
        ], (b, t) => {
          if (t > 0.18) b.body.rot(0.07 * settle(t - 0.18, 5, 10));
        }),
        // Aegis: the shield lifted high, the hammer held to the chest, braced.
        shot('skill1', s, 1.0, [
          [0.15, { armL: [-2.5, 0, 0.3], foreL: [-0.4, 0, 0], offhand: [-0.4, 0, 0], armR: [-0.5, 0, 0.1], foreR: [-1.4, 0, 0], body: [-0.1, 0, 0], head: [-0.3, 0, 0], legL: [-0.2, 0, -0.1], legR: [0.15, 0, 0.1], figure: [0, 0, 0, 0, -0.04, 0] }, outBack],
          [0.65, { armL: [-2.55, 0, 0.3], foreL: [-0.4, 0, 0], offhand: [-0.4, 0, 0], armR: [-0.5, 0, 0.1], foreR: [-1.4, 0, 0], body: [-0.12, 0, 0], head: [-0.32, 0, 0], legL: [-0.2, 0, -0.1], legR: [0.15, 0, 0.1], figure: [0, 0, 0, 0, -0.04, 0] }],
        ]),
        // Hallow: down on one knee, the hammer planted head-down before him.
        shot('skill2', s, 1.2, [
          [0.22, { legL: [-1.4, 0, 0], shinL: [1.45, 0, 0], legR: [0.2, 0, 0], shinR: [1.6, 0, 0], figure: [0, 0, 0, 0, -0.42, 0], body: [0.25, 0, 0], armR: [-1.1, 0, -0.2], foreR: [-0.3, 0, 0], weapon: [3.1, 0, 0], armL: [-0.5, 0, -0.1], head: [0.35, 0, 0] }, outBack],
          [0.8, { legL: [-1.4, 0, 0], shinL: [1.45, 0, 0], legR: [0.2, 0, 0], shinR: [1.6, 0, 0], figure: [0, 0, 0, 0, -0.42, 0], body: [0.28, 0, 0], armR: [-1.1, 0, -0.2], foreR: [-0.3, 0, 0], weapon: [3.1, 0, 0], armL: [-0.5, 0, -0.1], head: [0.4, 0, 0] }],
        ]),
      ];
    case 'barbarian':
      return [
        // Whirl: the axe out at arm's length and the whole figure spins round twice.
        shot('skill0', s, 0.9, [
          [0.08, { armR: [-1.4, 0, 0.9], foreR: [0.1, 0, 0], weapon: [1.6, 0, 0], armL: [-1.2, 0, -0.9], body: [0.1, 0.5, 0], figure: [0, 0.4, 0, 0, -0.06, 0], legL: [-0.2, 0, -0.2], legR: [0.2, 0, 0.2] }, outQuad],
          [0.62, { armR: [-1.45, 0, 1.0], foreR: [0.1, 0, 0], weapon: [1.6, 0, 0], armL: [-1.2, 0, -0.9], body: [0.1, -0.2, 0], figure: [0, -TAU * 2, 0, 0, -0.06, 0], legL: [-0.2, 0, -0.2], legR: [0.2, 0, 0.2] }, (k) => k],
        ]),
        // Leap: a crouch, a high arc, and the axe brought down as it lands.
        shot('skill1', s, 1.0, [
          [0.1, { legL: [-0.8, 0, 0], shinL: [1.3, 0, 0], legR: [-0.6, 0, 0], shinR: [1.2, 0, 0], figure: [0, 0, 0, 0, -0.22, 0], body: [0.5, 0, 0], armR: [0.4, 0, 0.2], armL: [0.5, 0, -0.2] }, outQuad],
          [0.32, { legL: [-0.6, 0, 0], shinL: [0.8, 0, 0], legR: [0.2, 0, 0], shinR: [1.1, 0, 0], figure: [0, 0, 0, 0, 0.75, 0.1], body: [-0.2, 0, 0], armR: [-2.8, 0, 0.1], foreR: [0.6, 0, 0], weapon: [-2.3, 0, 0], armL: [-2.6, 0, 0.3], head: [-0.2, 0, 0] }, outQuad],
          [0.5, { legL: [-0.9, 0, 0], shinL: [1.2, 0, 0], legR: [0.1, 0, 0], shinR: [1.3, 0, 0], figure: [0, 0, 0, 0, -0.3, 0.15], body: [0.65, 0, 0], armR: [-0.7, 0, -0.1], foreR: [1.5, 0, 0], weapon: [2.8, 0, 0], armL: [-0.8, 0, 0.4], head: [0.3, 0, 0] }, inCubic],
          [0.72, { legL: [-0.8, 0, 0], shinL: [1.0, 0, 0], legR: [0.15, 0, 0], shinR: [1.1, 0, 0], figure: [0, 0, 0, 0, -0.25, 0.15], body: [0.55, 0, 0], armR: [-0.7, 0, -0.1], foreR: [1.5, 0, 0], weapon: [2.8, 0, 0], armL: [-0.7, 0, 0.4], head: [0.25, 0, 0] }],
        ], undefined, (t) => {
          // Stretch on take-off, squash on landing.
          const st = t > 0.1 && t < 0.3 ? Math.sin(((t - 0.1) / 0.2) * Math.PI) : 0;
          const sq = t > 0.5 ? Math.max(0, settle(t - 0.5, 3, 8)) : 0;
          return [1 - 0.06 * st + 0.1 * sq, 1 + 0.1 * st - 0.14 * sq, 1 - 0.06 * st + 0.1 * sq];
        }),
        // Frenzy: arms thrown wide, chest out, head back in a roar; it shakes with it.
        shot('skill2', s, 1.0, [
          [0.14, { armR: [-0.4, 0, 1.3], foreR: [-0.8, 0, 0], weapon: [-0.2, 0, 0], armL: [-0.4, 0, -1.3], foreL: [-0.8, 0, 0], body: [-0.3, 0, 0], head: [-0.55, 0, 0], legL: [-0.15, 0, -0.18], legR: [0.15, 0, 0.18], figure: [0, 0, 0, 0, -0.06, 0] }, outBack],
          [0.7, { armR: [-0.45, 0, 1.35], foreR: [-0.9, 0, 0], weapon: [-0.2, 0, 0], armL: [-0.45, 0, -1.35], foreL: [-0.9, 0, 0], body: [-0.32, 0, 0], head: [-0.6, 0, 0], legL: [-0.15, 0, -0.18], legR: [0.15, 0, 0.18], figure: [0, 0, 0, 0, -0.06, 0] }],
        ], (b, t) => {
          const shake = t > 0.14 && t < 0.75 ? Math.sin(t * 90) * 0.035 : 0;
          b.body.rot(shake, shake * 0.5, 0);
          b.head.rot(shake * 1.5, 0, 0);
        }, (t) => {
          const k = t > 0.1 && t < 0.8 ? 0.05 : 0;
          return [1 + k, 1 + k * 0.5, 1 + k];
        }),
      ];
    case 'artificer':
      return [
        // Turret: down on one knee, the hammer sets the turret's pin.
        shot('skill0', s, 0.95, [
          [0.18, { legL: [-1.3, 0, 0], shinL: [1.4, 0, 0], legR: [0.25, 0, 0], shinR: [1.55, 0, 0], figure: [0, 0, 0, 0, -0.4, 0], body: [0.45, 0, 0], armR: [-2.3, 0, 0], foreR: [-0.6, 0, 0], weapon: [-1.3, 0, 0], armL: [-0.9, 0, 0.2], head: [0.4, 0, 0] }, outQuad],
          [0.3, { legL: [-1.3, 0, 0], shinL: [1.4, 0, 0], legR: [0.25, 0, 0], shinR: [1.55, 0, 0], figure: [0, 0, 0, 0, -0.4, 0], body: [0.6, 0, 0], armR: [-0.9, 0, 0], foreR: [-0.2, 0, 0], weapon: [2.4, 0, 0], armL: [-0.9, 0, 0.2], head: [0.5, 0, 0] }, inCubic],
          [0.6, { legL: [-1.3, 0, 0], shinL: [1.4, 0, 0], legR: [0.25, 0, 0], shinR: [1.55, 0, 0], figure: [0, 0, 0, 0, -0.4, 0], body: [0.5, 0, 0], armR: [-1.0, 0, 0], foreR: [-0.3, 0, 0], weapon: [2.2, 0, 0], armL: [-0.8, 0, 0.2], head: [0.4, 0, 0] }],
        ]),
        // Gear grenade: an overhand lob with the gauntlet hand.
        shot('skill1', s, 0.75, [
          [0.08, { armL: [0.6, 0, -0.3], foreL: [-1.6, 0, 0], body: [-0.1, -0.5, 0], head: [-0.1, 0.3, 0], legL: [0.2, 0, 0], legR: [-0.15, 0, 0] }, outQuad],
          [0.15, { armL: [-2.4, 0, 0.1], foreL: [-0.2, 0, 0], body: [0.2, 0.4, 0], head: [-0.15, -0.2, 0], ...STEP_IN }, outExpo],
          [0.32, { armL: [-1.4, 0, 0.2], foreL: [-0.3, 0, 0], body: [0.15, 0.3, 0], ...STEP_IN_HOLD }, outBack],
        ]),
        // Automaton: the hammer raised as a signal, then pointed where it drops.
        shot('skill2', s, 1.0, [
          [0.15, { armR: [-2.8, 0, 0.1], foreR: [-0.2, 0, 0], weapon: [0, 0, 0], head: [-0.4, 0, 0], body: [-0.12, 0, 0] }, outBack],
          [0.45, { armR: [-1.5, 0, 0], foreR: [-0.1, 0, 0], weapon: [1.4, 0, 0], head: [0.1, 0, 0], body: [0.1, 0, 0], ...STEP_IN_HOLD }, inOut],
          [0.75, { armR: [-1.45, 0, 0], foreR: [-0.1, 0, 0], weapon: [1.4, 0, 0], head: [0.1, 0, 0], body: [0.1, 0, 0], ...STEP_IN_HOLD }],
        ]),
      ];
    case 'mage':
      return [
        // Fireball: drawn back over the shoulder, then hurled from the staff.
        shot('skill0', s, 0.8, [
          [0.1, { armR: [-2.2, 0, 0.3], foreR: [-0.6, 0, 0], weapon: [-1.0, 0, 0], armL: [-1.0, 0, -0.3], foreL: [-1.0, 0, 0], body: [-0.2, 0.5, 0], head: [-0.1, -0.2, 0] }, outQuad],
          [0.18, { armR: [-1.3, 0, -0.2], foreR: [0, 0, 0], weapon: [1.3, 0, 0], armL: [-0.6, 0, 0.3], foreL: [-0.4, 0, 0], body: [0.3, -0.4, 0], head: [0.05, 0.2, 0], ...STEP_IN }, outExpo],
          [0.4, { armR: [-1.2, 0, -0.2], weapon: [1.2, 0, 0], armL: [-0.5, 0, 0.3], body: [0.25, -0.35, 0], ...STEP_IN_HOLD }, outBack],
        ]),
        // Frost wall: the staff swept low from side to side.
        shot('skill1', s, 0.9, [
          [0.1, { armR: [-1.0, 0, 1.0], foreR: [-0.3, 0, 0], weapon: [1.2, 0, 0], body: [0.2, 0.7, 0], head: [0.1, 0.4, 0], armL: [-0.8, 0, -0.5] }, outQuad],
          [0.32, { armR: [-1.0, 0, -0.8], foreR: [-0.3, 0, 0], weapon: [1.6, 0, 0], body: [0.3, -0.7, 0], head: [0.15, -0.4, 0], armL: [-0.5, 0, 0.2], ...STEP_IN_HOLD }, inOut],
          [0.5, { armR: [-0.95, 0, -0.85], foreR: [-0.3, 0, 0], weapon: [1.65, 0, 0], body: [0.28, -0.7, 0], head: [0.15, -0.4, 0], ...STEP_IN_HOLD }],
        ]),
        // Storm: both hands up, the staff to the sky, head thrown back.
        shot('skill2', s, 1.2, [
          [0.2, { ...RAISE_STAFF, armL: [-2.5, 0, 0.6], foreL: [-0.2, 0, 0] }, outBack],
          [0.85, { ...RAISE_STAFF, armL: [-2.55, 0, 0.65], foreL: [-0.2, 0, 0], head: [-0.5, 0, 0] }],
        ], (b, t) => {
          const k = t > 0.2 && t < 0.9 ? Math.sin(t * 60) * 0.02 : 0;
          b.armR.rot(k);
          b.armL.rot(-k);
        }),
      ];
    case 'cleric':
      return [
        // Mend: an open hand held out to the ally, the staff kept upright.
        shot('skill0', s, 0.9, [
          [0.16, { armL: [-1.35, 0, 0.15], foreL: [-0.15, 0, 0], offhand: [1.2, 0, 0], armR: [-0.2, 0, 0.1], head: [-0.05, -0.1, 0], body: [0.05, 0.2, 0] }, outBack],
          [0.6, { armL: [-1.4, 0, 0.15], foreL: [-0.15, 0, 0], offhand: [1.2, 0, 0], armR: [-0.2, 0, 0.1], head: [-0.05, -0.1, 0], body: [0.05, 0.2, 0] }],
        ]),
        // Sanctuary: the staff lifted and driven down into the ground.
        shot('skill1', s, 0.9, [
          [0.1, { armR: [-2.4, 0, 0], foreR: [-0.5, 0, 0], weapon: [0, 0, 0], armL: [-2.2, 0, 0], foreL: [-0.5, 0, 0], body: [-0.15, 0, 0], head: [-0.2, 0, 0], figure: [0, 0, 0, 0, 0.04, 0] }, outQuad],
          [0.18, { armR: [-1.0, 0, -0.2], foreR: [-0.5, 0, 0], weapon: [0, 0, 0], armL: [-1.0, 0, 0.3], foreL: [-0.6, 0, 0], body: [0.35, 0, 0], head: [0.25, 0, 0], figure: [0, 0, 0, 0, -0.1, 0], shinL: [0.4, 0, 0], shinR: [0.4, 0, 0], legL: [-0.3, 0, 0], legR: [-0.2, 0, 0] }, inCubic],
          [0.5, { armR: [-1.0, 0, -0.2], foreR: [-0.5, 0, 0], weapon: [0, 0, 0], armL: [-1.0, 0, 0.3], foreL: [-0.6, 0, 0], body: [0.3, 0, 0], head: [0.2, 0, 0], figure: [0, 0, 0, 0, -0.09, 0], shinL: [0.35, 0, 0], shinR: [0.35, 0, 0], legL: [-0.25, 0, 0], legR: [-0.18, 0, 0] }],
        ]),
        // Dawn: the sun-staff raised to the sky, held.
        shot('skill2', s, 1.3, [
          [0.25, RAISE_STAFF, outBack],
          [0.95, { ...RAISE_STAFF, head: [-0.48, 0, 0], body: [-0.18, 0, 0] }],
        ]),
      ];
    case 'druid':
      return [
        // Entangle: crouched, a hand pressed to the earth, then lifted as the roots rise.
        shot('skill0', s, 1.0, [
          [0.16, { legL: [-1.0, 0, 0], shinL: [1.3, 0, 0], legR: [-0.3, 0, 0], shinR: [1.2, 0, 0], figure: [0, 0, 0, 0, -0.32, 0], body: [0.7, 0, 0], armL: [-1.1, 0, 0.2], foreL: [0.1, 0, 0], head: [0.4, 0, 0] }, outQuad],
          [0.5, { legL: [-0.4, 0, 0], shinL: [0.5, 0, 0], legR: [0.1, 0, 0], shinR: [0.4, 0, 0], figure: [0, 0, 0, 0, -0.08, 0], body: [0.0, 0, 0], armL: [-2.2, 0, 0.5], foreL: [-0.3, 0, 0], head: [-0.25, 0, 0] }, outBack],
          [0.7, { legL: [-0.35, 0, 0], shinL: [0.45, 0, 0], legR: [0.1, 0, 0], shinR: [0.35, 0, 0], figure: [0, 0, 0, 0, -0.07, 0], armL: [-2.2, 0, 0.5], foreL: [-0.3, 0, 0], head: [-0.25, 0, 0] }],
        ]),
        // Thorn wall: an arm swept wide as the wall grows.
        shot('skill1', s, 0.9, [
          [0.1, { armL: [-0.8, 0, 0.9], foreL: [-0.4, 0, 0], body: [0.1, -0.5, 0], head: [0, -0.3, 0] }, outQuad],
          [0.34, { armL: [-1.4, 0, -1.0], foreL: [-0.1, 0, 0], body: [0.15, 0.6, 0], head: [0.05, 0.4, 0], ...STEP_IN_HOLD }, inOut],
          [0.55, { armL: [-1.35, 0, -1.05], foreL: [-0.1, 0, 0], body: [0.15, 0.6, 0], head: [0.05, 0.4, 0], ...STEP_IN_HOLD }],
        ]),
        // Bear form: hunched over, arms curled in, then thrown out in a roar.
        shot('skill2', s, 0.8, [
          [0.12, { body: [0.7, 0, 0], head: [0.4, 0, 0], armL: [-0.9, 0, 0.5], foreL: [-1.6, 0, 0], armR: [-0.9, 0, -0.5], foreR: [-1.6, 0, 0], legL: [-0.4, 0, 0], shinL: [0.7, 0, 0], legR: [-0.3, 0, 0], shinR: [0.6, 0, 0], figure: [0, 0, 0, 0, -0.15, 0] }, outQuad],
          [0.26, { body: [-0.3, 0, 0], head: [-0.5, 0, 0], armL: [-0.6, 0, -1.3], foreL: [-0.9, 0, 0], armR: [-0.6, 0, 1.3], foreR: [-0.9, 0, 0], legL: [-0.15, 0, -0.15], legR: [0.15, 0, 0.15], figure: [0, 0, 0, 0, -0.04, 0] }, outBack],
          [0.5, { body: [-0.28, 0, 0], head: [-0.45, 0, 0], armL: [-0.6, 0, -1.25], foreL: [-0.9, 0, 0], armR: [-0.6, 0, 1.25], foreR: [-0.9, 0, 0], legL: [-0.15, 0, -0.15], legR: [0.15, 0, 0.15] }],
        ], undefined, (t) => {
          const k = t > 0.2 ? 0.08 * Math.max(0, 1 - (t - 0.2) * 2) : 0;
          return [1 + k, 1 + k, 1 + k];
        }),
      ];
    case 'ranger':
      return [
        // Multishot: a fan of arrows, the draw swept across.
        shot('skill0', s, 0.8, [
          [0.08, { armL: [-1.45, 0.6, -0.1], offhand: [0.1, 0, 0.6], armR: [-1.4, -0.3, 0.3], foreR: [-2.0, 0, 0], body: [0, 0.9, 0], head: [0, -0.8, 0] }, outQuad],
          [0.16, { armL: [-1.45, 0.6, -0.1], offhand: [0.25, 0, 0.6], armR: [-1.1, -0.9, 0.9], foreR: [-0.9, 0, 0], body: [0, 0.9, 0], head: [0, -0.8, 0] }, outExpo],
          [0.32, { armL: [-1.45, -0.2, -0.1], offhand: [0.1, 0, 0.6], armR: [-1.4, -0.3, 0.3], foreR: [-2.0, 0, 0], body: [0, 0.4, 0], head: [0, -0.3, 0] }, inOut],
          [0.4, { armL: [-1.45, -0.2, -0.1], offhand: [0.25, 0, 0.6], armR: [-1.1, -0.9, 0.9], foreR: [-0.9, 0, 0], body: [0, 0.4, 0], head: [0, -0.3, 0] }, outExpo],
        ]),
        // Net: swung underhand and let fly.
        shot('skill1', s, 0.8, [
          [0.1, { armR: [0.9, 0, 0.3], foreR: [-0.4, 0, 0], body: [0.2, -0.4, 0], head: [0.1, 0.2, 0], legR: [0.3, 0, 0], shinR: [0.3, 0, 0] }, outQuad],
          [0.2, { armR: [-1.9, 0, -0.2], foreR: [-0.2, 0, 0], body: [-0.1, 0.3, 0], head: [-0.15, -0.1, 0], ...STEP_IN }, outExpo],
          [0.4, { armR: [-1.6, 0, -0.2], foreR: [-0.2, 0, 0], body: [-0.05, 0.25, 0], ...STEP_IN_HOLD }, outBack],
        ]),
        // Arrow rain: the bow aimed high at the sky, drawn and loosed.
        shot('skill2', s, 0.9, [
          [0.1, { armL: [-2.5, 0.2, -0.1], foreL: [0.05, 0, 0], offhand: [-0.9, 0, 0.15], armR: [-2.2, -0.4, 0.35], foreR: [-1.9, 0, 0], body: [-0.35, 0.6, 0], head: [-0.6, -0.5, 0] }, outQuad],
          [0.18, { armL: [-2.5, 0.2, -0.1], foreL: [0.05, 0, 0], offhand: [-0.75, 0, 0.15], armR: [-1.9, -0.9, 0.9], foreR: [-0.9, 0, 0], body: [-0.38, 0.6, 0], head: [-0.62, -0.5, 0] }, outExpo],
          [0.45, { armL: [-2.3, 0.2, -0.1], offhand: [-0.7, 0, 0.1], armR: [-1.5, -0.6, 0.6], foreR: [-0.8, 0, 0], body: [-0.3, 0.5, 0], head: [-0.5, -0.4, 0] }],
        ]),
      ];
    default:
      return [];
  }
};

const attackFor = (set: HeroClipSet, s: Style): Clip<HB> => {
  switch (set) {
    case 'warrior':
    case 'king':
      return swordCut(s);
    case 'paladin':
      return hammerBlow(s, 0.72);
    case 'artificer':
      return hammerBlow(s, 0.66);
    case 'barbarian':
      return axeChop(s);
    case 'ranger':
      return bowShot(s);
    case 'mage':
    case 'cleric':
    case 'druid':
      return staffShot(s);
    default:
      return punch(s);
  }
};

/** The clips of a class (or the boss king, or plain folk). */
export const humanClips = (set: HeroClipSet): Clip<HB>[] => {
  const s = STYLES[set];
  return [idle(s), walk(s, s.weight > 0.7 ? 0.92 : 0.84), attackFor(set, s), ...skillsFor(set, s), s.shield ? block(s) : flinch(s), down(s), work()];
};
